<template>
  <main-large-dialog
    no-paddings-x
    no-paddings-y
    dialog-width="80%"
    body-height="60vh"
    :is-visible.sync="computedIsVisible"
    :title="$i18n.t('dialog.layersManager')"
    @agree="addLayersHandler"
    agree-text="dialog.agreeAdd"
    :loading="loading || embeddedLayersLoading"
    :is-valid="isAnyLayerChecked"
  >
    <template #body>
      <main-loader v-if="dataLoading" style="height: 448px"></main-loader>
      <template v-else>
        <grey-tabs v-model="currentLayersTab" :tabs="greyTabs" :show-arrows="false" fill-height fill-width>
          <v-row no-gutters class="pl-3 pt-7 pb-6 tw:bg-white tw:z-1">
            <v-col cols="3">
              <data-input
                v-model="searchValue"
                :data-type="{ name: 'text' }"
                prepend-inner-icon="mdi-magnify"
                :placeholder="$i18n.t('default.find')"
                hide-details
                clearable
                class="tw:solo-input"
              />
            </v-col>
            <v-col cols="auto" class="pl-2" style="min-width: 200px" v-if="greyTabs[currentLayersTab].sorting">
              <data-input color="grey darken-1" class="tw:solo-input" v-model="sorting" v-bind="sortingInput">
                <template #selection>
                  <span class="font-weight-medium" style="font-size: 12px">{{ sortingInput.label }}</span>
                </template>
              </data-input>
            </v-col>
          </v-row>
          <v-tab-item v-for="(tab, idx) in filteredTabs" :key="tab.text" class="pt-0">
            <component
              v-if="tab.component"
              :is="tab.component"
              v-model="layersChecked[tab.name]"
              :search="searchValue"
              :sorting="sorting"
            />
            <v-treeview
              v-else-if="layersSchema[tab.name].length > 0"
              :ref="`tree-${idx}`"
              v-model="layersChecked[tab.name]"
              selectable
              :items="layersSchema[tab.name]"
              item-key="key"
              return-object
              :search="searchValue"
              :open-all="!!searchValue"
              open-on-click
              selected-color="primary"
              @input="refreshCheckedLayers"
            >
              <template #prepend="{ item }">
                <v-icon v-if="!item.children" size="20" color="rgba(0, 0, 0, 0.7)">
                  {{ getDataSourceIcon(item.geometry_type || item.service_type) }}
                </v-icon>
              </template>
              <template #append="{ item }">
                <v-tooltip
                  v-if="item.description"
                  bottom
                  max-width="500"
                  content-class="tooltip_multiline"
                  color="font"
                >
                  <template #activator="{ on }">
                    <v-icon v-on="on" small color="primary"> mdi-help-circle </v-icon>
                  </template>
                  <span>{{ item?.description }}</span>
                </v-tooltip>
              </template>
            </v-treeview>
            <v-row v-else class="fill-height justify-center align-center no-gutters">
              <v-col cols="auto">
                <v-subheader>
                  {{ $i18n.t('admin.noData') }}
                </v-subheader>
              </v-col>
            </v-row>
          </v-tab-item>
        </grey-tabs>
      </template>
    </template>
  </main-large-dialog>
</template>

<script>
import { call, get } from 'vuex-pathify';
import chain from 'lodash/chain';
import mixin from 'lodash/mixin';
import map from 'lodash/map';
import filter from 'lodash/filter';
import value from 'lodash/value';
import wrapperLodash from 'lodash/wrapperLodash';

mixin(wrapperLodash, {
  chain,
  map,
  filter,
});
wrapperLodash.value = value;
wrapperLodash.prototype.value = value;

import stylePreviewUtilities from '@/mixins/stylePreviewUtilities';

import MainLoader from '@/components/MainLoader';
import GreyTabs from '@/components/GreyTabs';
import MainLargeDialog from '@/components/MainLargeDialog';
import DataInput from '@/components/DataInput';

export default {
  name: 'LayersAdding',
  mixins: [stylePreviewUtilities],
  components: {
    MainLargeDialog,
    MainLoader,
    GreyTabs,
    DataInput,
  },
  props: {
    admin: {
      type: Boolean,
      default: false,
    },
    isLayersAddingVisible: {
      type: Boolean,
      required: true,
    },
    layers: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    currentLayersTab: 0,
    layersChecked: {},
    layersSchema: {},
    lastKey: 0,
    isLoaded: false,
    searchValue: '',
    sorting: 'name-asc',
    embeddedLayersLoading: false,
  }),
  computed: {
    sortingInput() {
      return {
        dataType: { name: 'select' },
        items: [
          { value: 'name-asc', text: this.$i18n.t('table.attributeStats.sorting.nameAsc') },
          { value: 'name-desc', text: this.$i18n.t('table.attributeStats.sorting.nameDesc') },
          { value: 'source-asc', text: this.$i18n.t('table.attributeStats.sorting.datasourceAsc') },
          { value: 'source-desc', text: this.$i18n.t('table.attributeStats.sorting.datasourceDesc') },
        ],
        label: this.$i18n.t('table.attributeStats.sorting.sorting'),
        dense: true,
        hideDetails: true,
        singleLine: true,
        prependInnerIcon: 'mdi-swap-vertical',
        height: '30',
        disabledSorting: true,
      };
    },

    layersAll() {
      return JSON.parse(JSON.stringify(this.$store.get('layers/layers')));
    },
    currentProjectId: get('layers/project.id'),
    cacheLayersMetadata: get('layers/cacheLayersMetadata'),
    mapPanelDataFetched: get('map/mapPanelDataFetched'),
    settings: get('admin/settingsValues'),
    permissions: get('users/toolsPermissions'),
    additionalModules: get('admin/additionalModules'),
    layersSchemaByScopeFull: get('layers/layersSchemaByScope'),
    layersSchemaByScope() {
      return Object.entries(this.layersSchemaByScopeFull).reduce((total, [scope, groups]) => {
        total[scope] = groups
          .map(group => {
            return { ...group, layers: group.layers.filter(layer => layer.visible) };
          })
          .filter(group => group.layers.length > 0);
        return total;
      }, {});
    },
    currentUser: get('users/currentUser'),
    dataLoading() {
      return !this.isLoaded || (!this.mapPanelDataFetched && !this.admin);
    },
    tabs() {
      return [
        {
          name: 'core',
          types: [{ layerScope: 'core', schemaScope: 'core', visible: this.layersSchemaByScope?.core || false }],
          text: this.$i18n.t('dialog.layersPublic'),
        },
        {
          name: 'databox',
          component: () => import('@/components/LayersAddingDataBox'),
          types: [{ visible: this.getIsTabVisible('DATABOX_DATA_MODULE', 'databox_vector_tiles') }],
          text: 'Data.Box',
          hasSorting: true,
        },
        {
          name: 'water',
          types: [
            {
              layerScope: 'water',
              schemaScope: 'water',
              visible: this.getIsTabVisible('WATER_DATA', 'water_data_module_enabled'),
            },
          ],
          text: this.$i18n.t('dialog.wodLayers'),
        },
        {
          name: 'sewer',
          types: [
            {
              layerScope: 'sewer',
              schemaScope: 'sewer',
              visible: this.getIsTabVisible('SEWER_DATA', 'sewer_data_module_enabled'),
            },
          ],
          text: this.$i18n.t('dialog.sewerLayers'),
        },
        {
          name: 'zdm_data',
          types: [{ layerScope: 'zdm_data', schemaScope: 'zdm_data', visible: this.getIsTabVisible('ZDM_DATA') }],
          text: this.$i18n.t('dialog.lightingData'),
        },
        {
          name: 'masterplan_squares',
          types: [
            {
              layerScope: 'masterplan_squares',
              schemaScope: 'masterplan_squares',
              visible: this.getIsTabVisible('MASTERPLAN_SQUARES', 'masterplan_squares_module_enabled'),
            },
            {
              layerScope: 'masterplan_illumination',
              schemaScope: 'masterplan_illumination',
              visible: this.getIsTabVisible('MASTERPLAN_ILLUMINATION', 'masterplan_illumination_module_enabled'),
            },
            {
              layerScope: 'masterplan_streets',
              schemaScope: 'masterplan_streets',
              visible: this.getIsTabVisible('MASTERPLAN_STREETS', 'masterplan_streets_module_enabled'),
            },
          ],
          text: this.$i18n.t('dialog.masterplan'),
        },
        {
          name: 'cache',
          types: [{ layerScope: 'cache', visible: this.getIsTabVisible('CACHE_LAYER_MODULE') }],
          text: this.$i18n.t('dialog.cacheLayers'),
        },
        {
          name: 'module',
          types: [{ layerScope: 'module', schemaScope: 'core', visible: this.layersSchemaByScope?.module || false }],
          text: this.$i18n.t('dialog.moduleLayers'),
        },
      ];
    },
    filteredTabs() {
      return this.tabs.filter(tab => tab.types?.find(type => type.visible));
    },
    filteredTabsWithTypesScope() {
      return this.filteredTabs.filter(tab => tab.types?.some(type => type.layerScope));
    },
    greyTabs() {
      return this.filteredTabs.map(tab => {
        return { name: tab.text, sorting: tab.hasSorting };
      });
    },
    isAnyLayerChecked() {
      return this.checkedLayers.length > 0;
    },
    checkedLayers() {
      return Object.keys(this.layersChecked)
        .reduce((total, current) => {
          return [...total, ...this.layersChecked[current]];
        }, [])
        .filter(l => l.type);
    },
    checkedLayersIds() {
      return (
        this.checkedLayers?.map(layer => {
          const { schema, id } = layer;
          return { schema, id };
        }) || []
      );
    },
    addedLayersIds() {
      return this.layers.map(l => l.layer_id);
    },
    computedIsVisible: {
      get() {
        return this.isLayersAddingVisible;
      },
      set(nV) {
        this.$emit('update:isLayersAddingVisible', nV);
      },
    },
  },
  watch: {
    isLayersAddingVisible(nv) {
      if (nv) {
        this.init();
      }
    },
    mapPanelDataFetched: {
      immediate: true,
      handler(nV) {
        if (nV) {
          this.init();
        }
      },
    },
    layersSchemaByScope: {
      deep: true,
      handler() {
        this.init();
      },
    },
    searchValue(nV) {
      for (const tab of Object.keys(this.$refs).filter(ref => ref.startsWith('tree'))) {
        this.$refs[tab][0].updateAll(!!nV);
      }
    },
  },
  methods: {
    addLayer: call('layers/addLayer'),
    getLayers: call('layers/getLayers'),
    getLayersSchemaByScope: call('layers/getLayersSchemaByScope'),
    getIsTabVisible(enableModuleName, enableSettingName) {
      const hasPermission = enableModuleName ? this.permissions[enableModuleName]?.main_value > 0 : true;
      const settingsValue = enableSettingName ? this.settings[enableSettingName] || false : true;
      return settingsValue && hasPermission && this.additionalModules[enableModuleName]?.enabled;
    },
    async addLayersHandler() {
      const checkedWholeGroups = this.$_objectToArray(this.layersSchema)
        .flat()
        .filter(element => {
          return (
            element.children?.length > 1 &&
            element.children?.every(layer => {
              return this.checkedLayersIds.find(checked => checked.id === layer.id && checked.schema === layer.schema);
            })
          );
        });
      this.embeddedLayersLoading = true;
      try {
        let checkedSoloLayers = await Promise.all(
          this.checkedLayers
            .filter(layer => {
              return !checkedWholeGroups.find(group => group.children.find(l => l.id === layer.id));
            })
            .map(async layer => {
              return layer.embedded ? await this[`getEmbedded${this.$_capitalize(layer.embedded)}Layer`](layer) : layer;
            })
            .filter(layer => !!layer)
        );
        this.$emit('addLayers', [...checkedWholeGroups, ...checkedSoloLayers]);
      } finally {
        this.embeddedLayersLoading = false;
      }
      this.layersChecked = this.tabs.reduce((total, current) => {
        return {
          ...total,
          [current.name]: [],
        };
      }, {});
    },
    async getEmbeddedDataboxLayer(layer) {
      const payload = {
        layer_type: 'service-layers',
        body: {
          name: layer.title,
          layer_scope: 'core',
          service_layers_names: [layer.name],
          url: layer.url,
          style_web: layer.style,
        },
        embedded: this.currentProjectId,
        skipMetadataParsing: true,
      };
      if (layer.type === 'raster')
        payload.body = {
          ...payload.body,
          service_type: 'xyz',
          parameters: Object.entries(layer.parameters).reduce((total, [key, value]) => {
            if (key === 'min_zoom') total['minZoom'] = value;
            else if (key === 'max_zoom') total['maxZoom'] = value;
            else total[key] = value;
            return total;
          }, {}),
        };
      else if (layer.type === 'vector') {
        payload.body = {
          ...payload.body,
          parameters: { geometry_type: this.getLayerGeometryTypeByStyle(layer.style), ...layer.parameters },
          service_type: 'mvt',
        };
      } else return undefined;
      const r = await this.addLayer(payload);
      return {
        id: r.id,
        type: 'service_layer',
        ...this.$_filterObjectKeys(payload.body, {
          allowedKeys: [
            'name',
            'layer_scope',
            'service_layers_names',
            'url',
            'style_web',
            'service_type',
            'parameters',
          ],
        }),
      };
    },
    checkLayer(layer) {
      if (!layer) {
        return false;
      } else if (this.addedLayersIds.includes(layer.id)) {
        return false;
      }
      return true;
    },
    checkCacheLayer(layerId) {
      return Boolean(this.cacheLayersMetadata[layerId.id]?.last_cache_datetime);
    },
    closeLayersAdding() {
      this.isLayersAddingVisible = false;
    },
    generateLayersGroup(layers, type, layerType) {
      let result = [];
      if (type === 'subgroups') {
        result = wrapperLodash(layers)
          .map(layer => {
            if (!layer) {
              return;
            }
            const data = {
              key: ++this.lastKey,
              id: layer.id,
              name: layer.name,
            };
            const childrenLayers = layer.layers ? this.generateLayersGroup(layer.layers, 'layers', layerType) : [];
            const childrenSubgroups = layer.subgroups
              ? this.generateLayersGroup(layer.subgroups, 'subgroups', layerType)
              : [];
            if (childrenLayers.length > 0 || childrenSubgroups.length > 0) {
              data.children = [...childrenLayers, ...childrenSubgroups];
            } else {
              return;
            }
            return data;
          })
          .filter(x => x)
          .value();
      } else if (type === 'layers') {
        result = wrapperLodash(layers)
          .map(layerId => {
            if (!layerId) {
              return;
            }
            const layer = { ...this.layersAll[layerId.id] };
            if (layerType === 'cache' && !this.checkCacheLayer(layerId)) {
              return;
            }
            if (!this.checkLayer(layer, layerType)) {
              return;
            }
            const data = layer;
            data.key = ++this.lastKey;
            data.schema = layerType;
            return data;
          })
          .filter(x => x)
          .value();
      }
      return result;
    },
    refreshCheckedLayers() {
      for (const schema of Object.keys(this.layersSchema)) {
        for (const element of this.layersSchema[schema]) {
          if (element.children) {
            for (const layer of element.children) {
              this.$set(
                layer,
                'disabled',
                this.checkedLayersIds.find(checked => checked.id === layer.id && checked.schema !== layer.schema)
                  ? true
                  : false
              );
            }
          } else {
            this.$set(
              element,
              'disabled',
              this.checkedLayersIds.find(checked => checked.id === element.id && checked.schema !== element.schema)
                ? true
                : false
            );
          }
        }
      }
    },
    async getLayersGroups() {
      this.isLoaded = false;
      this.layersSchema = this.filteredTabsWithTypesScope.reduce((total, current) => {
        let layers = [];
        for (const type of current.types) {
          layers = layers.concat(this.layersSchemaByScope?.[type.layerScope]);
        }
        return {
          ...total,
          [current.name]: this.generateLayersGroup(layers, 'subgroups', current.name),
        };
      }, {});
      this.isLoaded = true;
    },
    init() {
      this.searchValue = '';
      if (!this.mapPanelDataFetched && !this.admin) {
        return;
      }
      this.layersSchema = this.layersChecked = this.tabs.reduce((total, current) => {
        return {
          ...total,
          [current.name]: [],
        };
      }, {});
      this.getLayersGroups();
    },
  },
  mounted() {
    this.init();
  },
};
</script>

<style lang="scss" scoped>
::v-deep {
  .v-treeview-node__content {
    text-align: left;
  }

  .v-slide-group {
    z-index: 99;
    position: sticky;
    top: 0;
    @include border(bottom);
  }

  .v-tab {
    border: unset !important;
  }

  .v-treeview-node__checkbox {
    font-size: 18px;
    margin-left: 0px !important;
  }

  .v-treeview-node__toggle {
    color: rgba(0, 0, 0, 0.7);
  }

  .v-treeview-node__root {
    min-height: 35px;
  }

  .v-tabs-items {
    height: calc(100% - 48px);
    overflow: auto;
  }

  .v-window__container,
  .v-window-item {
    height: 100%;
  }

  .v-treeview-node__content {
    margin-left: 2px !important;
  }

  .v-treeview-node__label {
    display: flex;
    flex: 0 1 auto;
  }

  .v-treeview-node__prepend {
    min-width: 0;
  }
}
</style>
